import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { IconSize } from '@databox/icons/dist/types';

import type { Service } from 'webapp-types';
import type { BlockVisualization, Theme } from 'webapp/enums/enums';
import { PopupEvent } from 'webapp/enums/enums';

import BlockPopupVisualizationComponent from 'webapp/components/block/popup-visualization';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type { ChangeVisualizationWithPossibleConfirmFnReturnType } from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';

interface Args {
	blockState: BlockState;
	changeVisualization: (
		to: BlockVisualization,
	) => ChangeVisualizationWithPossibleConfirmFnReturnType;
	theme?: Theme | string;
	onlyCompatible?: boolean;
	availableVisualizations?: BlockVisualization[];
	updateIsActive?: (active: boolean) => void;
	iconSize?: IconSize;
}

export default class BlockContentVisualizationSwitchComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	@tracked isPopupOpen = false;

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup() {
		return this.block.blockSetup;
	}

	@cached
	get availableVisualizationOptions() {
		const baseList = this.block.switchableVisualizationList;

		const allSwitchableList = baseList.filter((visualization) => {
			const availableVisualizations = this.args.availableVisualizations;

			return (
				!visualization.isHidden &&
				(!availableVisualizations || availableVisualizations.includes(visualization.id))
			);
		});

		if (this.args.onlyCompatible) {
			const compatibleVisualizations =
				this.blockSetup.getCompatibleVisualizationsForCurrentVisualization();
			return allSwitchableList.filter((v) => {
				return compatibleVisualizations.includes(v.id);
			});
		} else {
			return allSwitchableList;
		}
	}

	get showSwitch() {
		return this.availableVisualizationOptions.length > 0;
	}

	get iconSize(): IconSize {
		return this.args.iconSize || 'dynamic';
	}

	@action
	openAvailableVisualizationList(e: PointerEvent) {
		if (this.popupService.isOpen(BlockPopupVisualizationComponent)) {
			return;
		}

		const attachTo = e.target as HTMLElement;

		const popup = this.popupService.createContext(BlockPopupVisualizationComponent, attachTo, {
			popupClass: 'popup-block-visualizations',
			neverFullwidth: true,
			theme: this.args.theme,
		});

		this.isPopupOpen = true;
		this.onIsPopupOpenChange();
		popup.open({
			selectedBlockSetup: this.blockSetup,
			onChange: this.args.changeVisualization,
			options: this.availableVisualizationOptions,
		});

		popup.on(PopupEvent.CLOSING, this, () => {
			this.isPopupOpen = false;
			this.onIsPopupOpenChange();
		});
	}

	private onIsPopupOpenChange() {
		this.args.updateIsActive?.(this.isPopupOpen);
	}
	<template>
		{{#if this.showSwitch}}
			<a
				class="visualization-switch {{if this.isPopupOpen 'active'}}"
				{{on "click" this.openAvailableVisualizationList}}
			>
				<CommonUiIconComponent @name="ChangeChartType" @size={{this.iconSize}} />
			</a>
		{{/if}}
	</template>
}
